import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { ArrowRight, ChevronLeft, ChevronRight, Search, X, Zap } from 'lucide-react';
import { GenericCard } from '@/components/molecules/GenericCard';
import { Loader } from '@/components/atoms/Loader';
import { getPokedex, getPokedexGenerations, getOwnedPokemons, getPokemonSpecies, type PokedexEntry, type OwnedPokemon, type EvolutionChainNode } from '@/services/pokemon';
import { cdnUrl } from '@/utils/cdn';
import { TYPE_COLORS } from '@/utils/pokemonTypes';

// ─── Constantes ───────────────────────────────────────────────────────────────

const GEN_LABELS: Record<string, string> = {
  'generation-i': 'Gén. I',
  'generation-ii': 'Gén. II',
  'generation-iii': 'Gén. III',
  'generation-iv': 'Gén. IV',
  'generation-v': 'Gén. V',
  'generation-vi': 'Gén. VI',
  'generation-vii': 'Gén. VII',
  'generation-viii': 'Gén. VIII',
  'generation-ix': 'Gén. IX',
};

const RARITY_COLORS: Record<number, string> = {
  0: 'bg-gray-300',
  1: 'bg-green-400',
  2: 'bg-blue-400',
  3: 'bg-purple-500',
  4: 'bg-yellow-400',
};

const POKEMON_TYPES = [
  'fire', 'water', 'grass', 'electric', 'ice', 'fighting', 'poison',
  'ground', 'flying', 'psychic', 'bug', 'rock', 'ghost', 'dragon',
  'dark', 'steel', 'fairy', 'normal',
];

// ─── Carte détail (tooltip/modal) ─────────────────────────────────────────────

interface PokedexDetailModalProps {
  entry: PokedexEntry;
  onClose: () => void;
}

const TRIGGER_LABELS: Record<string, string> = {
  'level-up': 'Niveau',
  'use-item': 'Pierre',
};

const triggerLabel = (trigger: string, level?: number, item?: string): string => {
  if (trigger === 'level-up' && level) return `Nv. ${level}`;
  if (trigger === 'use-item' && item) return item;
  return TRIGGER_LABELS[trigger] ?? trigger;
};

// Flatten a chain tree into an ordered array of stages for linear display
// For branching evolutions, we render the first branch inline and list others below
const flattenChain = (
  node: EvolutionChainNode,
  currentId: string,
): { id: string; realname: string; sprite: string; trigger?: string; level?: number; item?: string; isCurrent: boolean }[] => {
  const result: ReturnType<typeof flattenChain> = [];
  const walk = (n: EvolutionChainNode, edgeIn?: { trigger: string; level?: number; item?: string }) => {
    result.push({
      id: n.id,
      realname: n.realname,
      sprite: n.sprite,
      trigger: edgeIn?.trigger,
      level: edgeIn?.level,
      item: edgeIn?.item,
      isCurrent: n.id === currentId,
    });
    if (n.evolvesTo.length > 0) {
      walk(n.evolvesTo[0].node, n.evolvesTo[0]);
    }
  };
  walk(node);
  return result;
};

const PokedexDetailModal = ({ entry, onClose }: PokedexDetailModalProps) => {
  const navigate = useNavigate();

  const { data: ownedPokemon, isLoading: isLoadingOwned } = useQuery({
    queryKey: ['owned-by-species', entry.id],
    queryFn: () => getOwnedPokemons({ search: entry.realname, limit: 100 }),
    enabled: entry.isCaught,
    staleTime: 30_000,
    select: (data) => data.data.filter((p) => p.pokemonId === entry.id),
  });

  const { data: species } = useQuery({
    queryKey: ['species', entry.id],
    queryFn: () => getPokemonSpecies(entry.id),
    enabled: entry.isCaught,
    staleTime: 5 * 60_000,
  });

  const handleFusion = (pokemon: OwnedPokemon) => {
    onClose();
    navigate('/pokemons/fusion', { state: { sourcePokemon: pokemon } });
  };

  // Bouton fusion visible seulement si on a au moins 2 exemplaires
  const canFuse = (ownedPokemon?.length ?? 0) >= 2;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        className="bg-surface relative w-full max-w-sm overflow-y-auto rounded-3xl shadow-2xl"
        style={{ maxHeight: '90dvh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-secondary-500 px-6 pt-6 pb-5">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/40 transition-colors hover:text-white"
          >
            <X size={20} />
          </button>
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-white/10">
              <img
                src={cdnUrl(entry.sprite)}
                alt={entry.isCaught ? entry.realname : '???'}
                className="h-16 w-16 object-contain"
                style={entry.isCaught ? undefined : { filter: 'brightness(0) invert(0)' }}
                onError={(e) => { e.currentTarget.style.opacity = '0'; }}
              />
            </div>
            <div>
              <p className="font-lexend text-xs text-white/40">
                #{String(entry.pokedexId).padStart(3, '0')}
              </p>
              <h2 className="text-white-neutral font-lexend text-xl font-semibold">
                {entry.isCaught ? entry.realname : '???'}
              </h2>
              {entry.isCaught && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {entry.types.map((t) => (
                    <span
                      key={t}
                      className={`${TYPE_COLORS[t] ?? 'bg-gray-400'} rounded px-2 py-0.5 text-xs font-semibold text-white capitalize`}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {entry.isCaught ? (
            <>
              {/* Rareté + date */}
              <div className="flex items-center justify-between">
                <span className="text-content/60 text-sm">Rareté</span>
                <span className={`${RARITY_COLORS[entry.rarity] ?? 'bg-gray-300'} inline-block h-3 w-3 rounded-full`} />
              </div>
              {entry.caughtAt && (
                <div className="flex items-center justify-between">
                  <span className="text-content/60 text-sm">Découvert le</span>
                  <span className="text-content text-sm font-semibold">
                    {new Date(entry.caughtAt).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}

              {/* Description */}
              {species?.description && (
                <div className="rounded-2xl bg-raised p-3">
                  <p className="text-content/70 text-sm leading-relaxed italic">
                    "{species.description}"
                  </p>
                </div>
              )}

              {/* Chaîne d'évolution complète */}
              {species?.fullChain && (
                <div className="border-t border-line-soft pt-4">
                  <p className="text-content/60 mb-3 text-xs font-semibold uppercase tracking-wide">
                    Chaîne d'évolution
                  </p>
                  <div className="flex items-center gap-1 overflow-x-auto pb-1">
                    {flattenChain(species.fullChain, entry.id).map((node, i) => (
                      <div key={node.id} className="flex items-center gap-1 shrink-0">
                        {i > 0 && (
                          <div className="flex flex-col items-center px-1">
                            <ArrowRight size={12} className="text-content/30" />
                            <span className="text-content/40 text-[9px] leading-none mt-0.5 max-w-[40px] text-center truncate">
                              {triggerLabel(node.trigger ?? 'level-up', node.level, node.item)}
                            </span>
                          </div>
                        )}
                        <div className={`flex flex-col items-center gap-1 rounded-xl p-2 transition-colors ${
                          node.isCurrent
                            ? 'bg-primary-500/15 ring-2 ring-primary-400'
                            : 'bg-raised'
                        }`}>
                          <div className="flex h-10 w-10 items-center justify-center">
                            <img
                              src={cdnUrl(node.sprite)}
                              alt={node.realname}
                              className="h-9 w-9 object-contain"
                              onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                            />
                          </div>
                          <p className={`font-lexend text-[10px] font-semibold text-center leading-tight max-w-[52px] truncate ${
                            node.isCurrent ? 'text-primary-500' : 'text-content'
                          }`}>
                            {node.realname}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pokémon possédés */}
              <div className="border-t border-line-soft pt-4">
                <p className="text-content/60 mb-2 text-xs font-semibold uppercase tracking-wide">
                  Dans mon PC
                  {ownedPokemon && ownedPokemon.length > 0 && (
                    <span className="ml-1.5 text-accent">({ownedPokemon.length})</span>
                  )}
                </p>
                {isLoadingOwned ? (
                  <div className="flex justify-center py-3">
                    <Loader size={20} color="#f29602" />
                  </div>
                ) : ownedPokemon && ownedPokemon.length > 0 ? (
                  <div className="flex max-h-64 flex-col gap-2 overflow-y-auto">
                    {ownedPokemon.map((p) => (
                      <div
                        key={p.ownedPokemonId}
                        className="group rounded-xl bg-raised p-2 transition-all duration-200"
                      >
                        {/* Ligne principale */}
                        <div className="flex items-center gap-3">
                          <img
                            src={cdnUrl(p.isShiny ? p.pokemon.spriteShiny : p.pokemon.sprite)}
                            alt={p.nickname}
                            className="h-10 w-10 shrink-0 object-contain"
                            onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="font-lexend text-content truncate text-sm font-semibold">
                              {p.nickname}{p.isShiny && <span className="ml-1 text-[10px]">✨</span>}
                            </p>
                            <p className="text-content/50 text-xs">Nv.&nbsp;{p.level}</p>
                          </div>
                          {canFuse && (
                            <button
                              onClick={() => handleFusion(p)}
                              className="flex shrink-0 items-center gap-1 rounded-lg bg-secondary-500 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-secondary-600"
                            >
                              <Zap size={11} />
                              Fusion
                            </button>
                          )}
                        </div>

                        {/* Stats au hover */}
                        <div className="grid max-h-0 grid-cols-3 gap-x-3 gap-y-1 overflow-hidden px-1 text-[11px] opacity-0 transition-all duration-200 group-hover:max-h-24 group-hover:mt-2 group-hover:opacity-100">
                          {/* Item */}
                          <div className="col-span-3 flex items-center gap-1.5">
                            <span className="text-content/40">Item</span>
                            <span className="text-content/80 font-semibold truncate">
                              {p.heldItem ? p.heldItem.name : '—'}
                            </span>
                          </div>
                          {/* IVs */}
                          {[
                            { label: 'PV',    value: p.ivHp },
                            { label: 'Atk',   value: p.ivAtk },
                            { label: 'Déf',   value: p.ivDef },
                            { label: 'AtqSp', value: p.ivSpAtk },
                            { label: 'DéfSp', value: p.ivSpDef },
                            { label: 'Vit',   value: p.ivSpe },
                          ].map(({ label, value }) => (
                            <div key={label} className="flex items-center gap-1">
                              <span className="text-content/40 shrink-0">{label}</span>
                              <span className={`font-semibold tabular-nums ${
                                value === 31 ? 'text-yellow-400' :
                                value >= 26  ? 'text-green-400' :
                                value <= 10  ? 'text-red-400' :
                                'text-content/70'
                              }`}>
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-content/40 text-xs">Aucun dans votre PC.</p>
                )}
              </div>
            </>
          ) : (
            <p className="text-content/50 text-center text-sm">
              Ce Pokémon n'a pas encore été capturé.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Barres de progression par génération ─────────────────────────────────────

const GenerationStats = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['pokedex-generations'],
    queryFn: getPokedexGenerations,
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Loader size={24} color="#f29602" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {data.map((gen) => {
        const pct = gen.total > 0 ? Math.round((gen.caught / gen.total) * 100) : 0;
        const label = GEN_LABELS[gen.generation] ?? gen.generation;
        return (
          <div key={gen.generation} className="rounded-xl bg-raised p-3">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-content font-lexend text-xs font-semibold">{label}</span>
              <span className="text-content/50 text-xs">
                {gen.caught}
                <span className="text-content/30">/{gen.total}</span>
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-raised">
              <div
                className="from-primary-500 to-secondary-500 h-2 rounded-full bg-linear-to-r transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-content/40 mt-0.5 text-right text-[10px]">{pct}%</p>
          </div>
        );
      })}
    </div>
  );
};

// ─── Carte Pokémon ────────────────────────────────────────────────────────────

interface PokedexCardProps {
  entry: PokedexEntry;
  onClick: () => void;
}

const PokedexCard = ({ entry, onClick }: PokedexCardProps) => (
  <button
    onClick={onClick}
    className={`group relative flex flex-col items-center rounded-2xl border border-transparent p-3 shadow-sm transition-all duration-200 hover:shadow-lg ${
      entry.isCaught
        ? 'bg-surface hover:border-primary-300'
        : 'bg-raised hover:border-line-mid'
    }`}
  >
    <p className="text-content/30 font-lexend mb-1 text-[10px]">
      #{String(entry.pokedexId).padStart(3, '0')}
    </p>
    <div
      className={`mb-2 flex h-16 w-16 items-center justify-center rounded-xl ${
        entry.isCaught ? 'bg-primary-500/10' : 'bg-raised'
      }`}
    >
      <img
        src={cdnUrl(entry.sprite)}
        alt={entry.isCaught ? entry.realname : '???'}
        loading="lazy"
        className="h-14 w-14 object-contain transition-transform duration-200 group-hover:scale-110"
        style={entry.isCaught ? undefined : { filter: 'brightness(0)' }}
        onError={(e) => { e.currentTarget.style.opacity = '0'; }}
      />
    </div>
    <p className={`font-lexend w-full truncate text-center text-xs font-semibold ${
      entry.isCaught ? 'text-content' : 'text-content/30'
    }`}>
      {entry.isCaught ? entry.realname : '???'}
    </p>
    {entry.isCaught && (
      <div className="mt-1 flex flex-wrap justify-center gap-1">
        {entry.types.map((t) => (
          <span
            key={t}
            className={`${TYPE_COLORS[t] ?? 'bg-gray-400'} rounded px-1 py-0.5 text-[9px] font-semibold text-white capitalize`}
          >
            {t}
          </span>
        ))}
      </div>
    )}
    {/* Indicateur de rareté */}
    <span className={`absolute top-2 right-2 h-2 w-2 rounded-full ${RARITY_COLORS[entry.rarity] ?? 'bg-gray-300'}`} />
  </button>
);

// ─── Vue principale ───────────────────────────────────────────────────────────

export const Pokedex = () => {
  const { pokemonId } = useParams<{ pokemonId?: string }>();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [caught, setCaught] = useState<'all' | 'caught' | 'uncaught'>('all');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState<25 | 50 | 100>(25);
  const [selected, setSelected] = useState<PokedexEntry | null>(null);

  // Auto-open modal when coming from Discord bot link (/pokedex/:pokemonId)
  const { data: paramEntry } = useQuery({
    queryKey: ['pokedex-param', pokemonId],
    queryFn: () => getPokedex({ search: pokemonId, limit: 1 }),
    enabled: !!pokemonId,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (pokemonId && paramEntry?.data?.[0]) {
      setSelected(paramEntry.data[0]);
      navigate('/pokemons/pokedex', { replace: true });
    }
  }, [pokemonId, paramEntry, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['pokedex', offset, limit, debouncedSearch, selectedType, caught],
    queryFn: () =>
      getPokedex({
        offset,
        limit,
        search: debouncedSearch || undefined,
        type: selectedType || undefined,
        caught,
      }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const total = data?.total ?? 0;
  const caughtCount = data?.caughtCount ?? 0;
  const totalPokemon = data?.totalPokemon ?? 0;
  const entries = data?.data ?? [];
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const globalProgress = totalPokemon > 0 ? Math.round((caughtCount / totalPokemon) * 100) : 0;

  return (
    <main className="bg-page min-h-screen">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-5">

        {/* Header progression globale */}
        <GenericCard
          title="Pokédex"
          subtitle={`${caughtCount} / ${totalPokemon} espèces découvertes`}
        >
          <div className="mt-2 mb-4">
            <div className="bg-primary-500/20 h-3 overflow-hidden rounded-full">
              <div
                className="from-primary-500 to-secondary-500 h-3 rounded-full bg-linear-to-r transition-all duration-500"
                style={{ width: `${globalProgress}%` }}
              />
            </div>
            <p className="text-content/60 mt-1.5 text-right text-xs font-semibold">
              {globalProgress}%
            </p>
          </div>

          {/* Progression par génération */}
          <GenerationStats />
        </GenericCard>

        {/* Liste avec filtres */}
        <GenericCard title="Tous les Pokémon">
          {/* Filtres */}
          <div className="mb-3 flex flex-col gap-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <form
                role="search"
                className="relative max-w-xs flex-1"
                onSubmit={(e) => e.preventDefault()}
              >
                <Search
                  size={15}
                  className="text-content/40 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
                />
                <input
                  type="text"
                  aria-label="Rechercher un Pokémon"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Rechercher..."
                  className="text-content placeholder:text-content/40 focus:ring-primary-400 h-10 w-full rounded-xl border border-line-soft bg-raised pr-8 pl-9 text-sm focus:ring-2 focus:outline-none"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput('')}
                    className="text-content/40 hover:text-content/70 absolute top-1/2 right-2.5 -translate-y-1/2 transition-colors"
                    aria-label="Effacer"
                  >
                    <X size={14} />
                  </button>
                )}
              </form>

              <div className="flex flex-wrap gap-2">
                {/* Filtre type */}
                <select
                  value={selectedType}
                  onChange={(e) => { setSelectedType(e.target.value); setOffset(0); }}
                  className="text-content h-10 rounded-xl border border-line-soft bg-raised px-3 text-sm focus:outline-none"
                >
                  <option value="">Tous les types</option>
                  {POKEMON_TYPES.map((t) => (
                    <option key={t} value={t} className="capitalize">{t}</option>
                  ))}
                </select>

                {/* Filtre capturé */}
                <div className="flex gap-1">
                  {(['all', 'caught', 'uncaught'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => { setCaught(v); setOffset(0); }}
                      className={`h-10 rounded-xl px-3 text-sm font-semibold transition-colors ${
                        caught === v
                          ? 'bg-primary-500 text-white'
                          : 'text-content/60 border border-line-soft bg-raised hover:bg-line-soft'
                      }`}
                    >
                      {v === 'all' ? 'Tous' : v === 'caught' ? 'Capturés' : 'Manquants'}
                    </button>
                  ))}
                </div>

                {/* Taille de page */}
                <div className="flex gap-1">
                  {([25, 50, 100] as const).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => { setLimit(n); setOffset(0); }}
                      className={`h-10 rounded-xl px-3 text-sm font-semibold transition-colors ${
                        limit === n
                          ? 'bg-secondary-500 text-white'
                          : 'text-content/60 border border-line-soft bg-raised hover:bg-line-soft'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <p className="text-content/50 mb-4 text-xs">
            {data
              ? `${total} Pokémon${total !== 1 ? 's' : ''}`
              : '\u00A0'}
          </p>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader size={40} color="#f29602" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-4 py-16">
              <p className="text-content/60 text-sm">Impossible de charger le Pokédex.</p>
              <button
                onClick={() => refetch()}
                className="text-primary-500 hover:text-primary-600 text-sm font-semibold underline"
              >
                Réessayer
              </button>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <p className="text-5xl">🔍</p>
              <p className="text-content/50 text-sm">Aucun Pokémon trouvé.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
              {entries.map((entry) => (
                <PokedexCard
                  key={entry.id}
                  entry={entry}
                  onClick={() => setSelected(entry)}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => { setOffset(Math.max(0, offset - limit)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={offset === 0}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-line-soft bg-surface text-content/60 shadow-sm transition hover:bg-raised disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-content font-lexend text-sm font-semibold">
                {currentPage}&nbsp;/&nbsp;{totalPages}
              </span>
              <button
                type="button"
                onClick={() => { setOffset(offset + limit); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={offset + limit >= total}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-line-soft bg-surface text-content/60 shadow-sm transition hover:bg-raised disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </GenericCard>
      </div>

      {selected && (
        <PokedexDetailModal entry={selected} onClose={() => setSelected(null)} />
      )}
    </main>
  );
};
